import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { documentChunkService } from '@/services';
import type { ServiceContext } from '@prolarus/shared';

interface ChunkViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachmentId: string;
  attachmentName: string;
  sourceEntityType: string;
  sourceEntityId: string;
  context: ServiceContext;
  onRevectorize: () => void;
  onDeleteChunks: () => void;
  isVectorizing?: boolean;
}

const chunkTypeBadgeStyles: Record<string, string> = {
  terms: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  pricing_table: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  general_info: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  footnote: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  surcharge_rules: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

export default function ChunkViewerDialog({
  open,
  onOpenChange,
  attachmentId,
  attachmentName,
  onRevectorize,
  onDeleteChunks,
  isVectorizing,
}: ChunkViewerDialogProps) {
  const [expandedChunks, setExpandedChunks] = useState<Set<number>>(new Set());

  const { data: chunks = [], isLoading } = useQuery({
    queryKey: ['document-chunks', attachmentId],
    queryFn: async () => {
      const result = await documentChunkService.listByAttachment(attachmentId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: open && !!attachmentId,
  });

  const toggleChunk = (index: number) => {
    setExpandedChunks(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const truncateText = (text: string, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Document Chunks</DialogTitle>
          <DialogDescription>
            {attachmentName} — {chunks.length} chunks
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onRevectorize}
            disabled={isVectorizing}
          >
            {isVectorizing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Re-vectorize
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onDeleteChunks}
            disabled={isVectorizing}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Chunks
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : chunks.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No chunks found for this document.
          </div>
        ) : (
          <ScrollArea className="h-[50vh]">
            <div className="space-y-3 pr-4">
              {chunks.map((chunk) => {
                const isExpanded = expandedChunks.has(chunk.chunk_index);
                const metadata = chunk.chunk_metadata;
                return (
                  <div
                    key={chunk.id}
                    className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">
                          #{chunk.chunk_index}
                        </span>
                        {metadata?.section_header && (
                          <span className="text-sm font-medium">{metadata.section_header}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {metadata?.page_number && (
                          <span className="text-xs text-muted-foreground">
                            Page {metadata.page_number}
                          </span>
                        )}
                        {metadata?.chunk_type && (
                          <Badge
                            variant="secondary"
                            className={chunkTypeBadgeStyles[metadata.chunk_type] || ''}
                          >
                            {metadata.chunk_type.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <button
                      className="text-left w-full"
                      onClick={() => toggleChunk(chunk.chunk_index)}
                    >
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {isExpanded ? chunk.chunk_text : truncateText(chunk.chunk_text)}
                      </p>
                      {chunk.chunk_text.length > 200 && (
                        <span className="text-xs text-primary mt-1 inline-block">
                          {isExpanded ? 'Show less' : 'Show more'}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
